import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import API from "../services/api";
import {
  Card,
  Button,
  Spinner,
  Alert,
  Modal,
  Form,
  Row,
  Col,
} from "react-bootstrap";
import "./AdminEmployeeList.css";
import "./AdminVendorList.css";
import { downloadEmployeeAgreementPdf } from "../utils/pdfDocs";

/** tiny helpers */
const V_NONE = "__NONE__"; // special value for "None (Unassign all)"
const supplierLabel = (supplier = {}) =>
  supplier.companyName ||
  supplier.supplierName ||
  supplier.profile?.companyName ||
  supplier.profile?.supplierName ||
  supplier.fullName ||
  supplier.email ||
  "";
const initials = (name = "") => {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "EM";
  const a = parts[0]?.[0] || "";
  const b = parts[1]?.[0] || "";
  return (a + b).toUpperCase() || "EM";
};
const getAvatarTone = (seed = "") => {
  const tones = ["blue", "green", "amber", "violet", "rose", "sky"];
  const total = String(seed || "").split("").reduce((sum, ch) => sum + ch.charCodeAt(0), 0);
  return tones[total % tones.length];
};

const API_ORIGIN = process.env.REACT_APP_API_ORIGIN || "";
const toFileURL = (raw) => {
  if (!raw) return "";
  const v = String(raw).trim();
  if (!v) return "";
  if (/^https?:\/\//i.test(v)) return v;
  if (v.startsWith("/")) return `${API_ORIGIN}${v}`;
  return `${API_ORIGIN}/uploads/${v}`;
};

const reverseGeocode = async (lat, lng) => {
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${encodeURIComponent(
        lat
      )}&lon=${encodeURIComponent(lng)}`
    );
    if (!res.ok) return "";
    const data = await res.json();
    return data?.display_name || "";
  } catch {
    return "";
  }
};

const geocodeAddress = async (query) => {
  const q = String(query || "").trim();
  if (!q) return null;
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/search?format=jsonv2&q=${encodeURIComponent(q)}&limit=1`
    );
    if (!res.ok) return null;
    const data = await res.json();
    const first = Array.isArray(data) ? data[0] : null;
    const lat = Number(first?.lat);
    const lng = Number(first?.lon);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    return { lat, lng };
  } catch {
    return null;
  }
};

const AdminEmployeeList = () => {
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");

  // filters
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // all | approved | pending

  // modal state
  const [showModal, setShowModal] = useState(false);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
  const [selectedEmployeeName, setSelectedEmployeeName] = useState("");
  const [selectedVendors, setSelectedVendors] = useState([]);
  const [selectedSuppliers, setSelectedSuppliers] = useState([]);
  const [showWorkModal, setShowWorkModal] = useState(false);
  const [workEmployeeId, setWorkEmployeeId] = useState(null);
  const [workEmployeeName, setWorkEmployeeName] = useState("");
  const [workForm, setWorkForm] = useState({
    shiftStart: "",
    shiftEnd: "",
    salaryMonthly: "",
    hourlyRate: "",
    overtimeRate: "",
    lateGraceMinutes: "",
    attendanceMode: "office",
    officeAddress: "",
    officeRadiusMeters: "200",
    officeLat: "",
    officeLng: "",
  });
  const [showDocsModal, setShowDocsModal] = useState(false);
  const [docsEmployeeName, setDocsEmployeeName] = useState("");
  const [docsEmployeeDocs, setDocsEmployeeDocs] = useState({});
  const [docsEmployeeBank, setDocsEmployeeBank] = useState({});
  const [docsEmployeePhoto, setDocsEmployeePhoto] = useState("");
  const [showAgreementModal, setShowAgreementModal] = useState(false);
  const [agreementEmployee, setAgreementEmployee] = useState(null);
  const [locLoading, setLocLoading] = useState(false);

  const docInventory = useMemo(
    () => [
      { key: "aadhar", label: "Aadhar Card", icon: "badge" },
      { key: "pan", label: "PAN Card", icon: "credit_card" },
      { key: "addressProof", label: "Address Proof", icon: "home_pin" },
      { key: "resume", label: "Resume / CV", icon: "description" },
    ],
    []
  );

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      const res = await API.get("/admin/employees");
      setEmployees(res.data || []);
    } catch (err) {
      console.error(err);
      setError("Failed to load employees");
    } finally {
      setLoading(false);
    }
  };

  const fetchVendors = async () => {
    try {
      const res = await API.get("/admin/vendors");
      setVendors((res.data || []).sort((a, b) =>
        (a.fullName || a.email || "").localeCompare(b.fullName || b.email || "")
      ));
    } catch (err) {
      console.error("Failed to load vendors", err);
    }
  };

  const fetchSuppliers = async () => {
    try {
      const res = await API.get("/admin/suppliers");
      const rows = Array.isArray(res.data) ? res.data : res.data?.suppliers || [];
      setSuppliers(rows.sort((a, b) =>
        supplierLabel(a).localeCompare(supplierLabel(b))
      ));
    } catch (err) {
      console.error("Failed to load suppliers", err);
    }
  };

  useEffect(() => {
    fetchEmployees();
    fetchVendors();
    fetchSuppliers();
  }, []);

  const stats = useMemo(() => {
    const total = employees.length;
    const approved = employees.filter((e) => e.approved).length;
    const pending = total - approved;
    const signed = employees.filter((e) => e.employeeAgreementSummary?.accepted || e.employeeAgreement?.accepted).length;
    return { total, approved, pending, signed, unsigned: total - signed };
  }, [employees]);

  const exportAgreementStatusCsv = () => {
    const rows = [
      [
        "Employee Name",
        "Mobile",
        "Email",
        "Department",
        "Approved",
        "Agreement Status",
        "Signed By",
        "Accepted At",
        "Version",
      ],
      ...filteredEmployees.map((emp) => {
        const summary = emp.employeeAgreementSummary || {};
        return [
          emp.fullName || "",
          emp.mobile || "",
          emp.email || "",
          emp.department?.name || "",
          emp.approved ? "Approved" : "Pending",
          summary.accepted ? "Signed" : "Pending",
          summary.signName || "",
          summary.acceptedAt ? new Date(summary.acceptedAt).toLocaleString("en-IN") : "",
          summary.version || 1,
        ];
      }),
    ];
    const csv = rows
      .map((row) =>
        row
          .map((cell) => `"${String(cell ?? "").replace(/"/g, '""')}"`)
          .join(",")
      )
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `employee_agreement_status_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleApprove = async (id) => {
    if (!window.confirm("Approve this employee?")) return;
    try {
      setActionLoading(id);
      await API.put(`/admin/employees/${id}/approve`);
      await fetchEmployees();
      setInfo("Employee approved");
    } catch {
      setError("Failed to approve employee");
    } finally {
      setActionLoading(null);
    }
  };

  const handleTerminate = async (id) => {
    if (!window.confirm("Terminate this employee?")) return;
    try {
      setActionLoading(id);
      await API.put(`/admin/employees/${id}/terminate`);
      await fetchEmployees();
      setInfo("Employee terminated");
    } catch {
      setError("Failed to terminate employee");
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this employee? This action cannot be undone.")) return;
    try {
      setActionLoading(id);
      await API.delete(`/admin/employees/${id}`);
      await fetchEmployees();
      setInfo("Employee deleted");
    } catch {
      setError("Failed to delete employee");
    } finally {
      setActionLoading(null);
    }
  };

  const handleSendWelcome = async (id) => {
    try {
      setActionLoading(id);
      const res = await API.post(`/admin/whatsapp/send-registration-welcome/${id}`);
      setInfo(res.data?.message || "Welcome WhatsApp sent");
    } catch (e) {
      setError(e?.response?.data?.message || "Failed to send welcome WhatsApp");
    } finally {
      setActionLoading(null);
    }
  };

  const openAssignModal = (emp) => {
    setSelectedEmployeeId(emp._id);
    setSelectedEmployeeName(emp.fullName || emp.email || "Employee");
    setSelectedVendors((emp.assignedVendors || []).map((v) => v._id));
    setSelectedSuppliers((emp.assignedSuppliers || []).map((s) => s._id));
    setShowModal(true);
  };

  const openWorkModal = (emp) => {
    setWorkEmployeeId(emp._id);
    setWorkEmployeeName(emp.fullName || emp.email || "Employee");
    const wp = emp.workProfile || {};
    setWorkForm({
      shiftStart: wp.shiftStart || "",
      shiftEnd: wp.shiftEnd || "",
      salaryMonthly: wp.salaryMonthly ?? "",
      hourlyRate: wp.hourlyRate ?? "",
      overtimeRate: wp.overtimeRate ?? "",
      lateGraceMinutes: wp.lateGraceMinutes ?? "",
      attendanceMode: wp.attendanceMode || "office",
      officeAddress: wp.officeAddress || "",
      officeRadiusMeters: wp.officeRadiusMeters ?? "200",
      officeLat: wp.officeLocation?.lat ?? "",
      officeLng: wp.officeLocation?.lng ?? "",
    });
    setShowWorkModal(true);
  };

  const handleSaveWorkProfile = async () => {
    try {
      let officeLocation = null;
      if (workForm.officeLat !== "" && workForm.officeLng !== "") {
        officeLocation = { lat: Number(workForm.officeLat), lng: Number(workForm.officeLng) };
      } else if (String(workForm.officeAddress || "").trim()) {
        officeLocation = await geocodeAddress(workForm.officeAddress);
      }
      const payload = {
        shiftStart: workForm.shiftStart,
        shiftEnd: workForm.shiftEnd,
        salaryMonthly: workForm.salaryMonthly,
        hourlyRate: workForm.hourlyRate,
        overtimeRate: workForm.overtimeRate,
        lateGraceMinutes: workForm.lateGraceMinutes,
        attendanceMode: workForm.attendanceMode,
        officeAddress: workForm.officeAddress,
        officeRadiusMeters: workForm.officeRadiusMeters,
        officeLocation,
      };
      await API.put(`/admin/employees/${workEmployeeId}/work-profile`, payload);
      setShowWorkModal(false);
      await fetchEmployees();
      setInfo("Work profile updated");
    } catch {
      setError("Failed to update work profile");
    }
  };

  const fillOfficeFromCurrentLocation = () => {
    if (!navigator?.geolocation) {
      setError("Geolocation not supported in browser");
      return;
    }
    setLocLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const lat = Number(pos.coords.latitude).toFixed(7);
        const lng = Number(pos.coords.longitude).toFixed(7);
        const address = await reverseGeocode(lat, lng);
        setWorkForm((prev) => ({
          ...prev,
          officeLat: lat,
          officeLng: lng,
          officeAddress: address || prev.officeAddress,
        }));
        setLocLoading(false);
      },
      () => {
        setLocLoading(false);
        setError("Unable to fetch current location. Please allow location access.");
      },
      { enableHighAccuracy: true, timeout: 15000 }
    );
  };

  const openDocsModal = (emp) => {
    setDocsEmployeeName(emp.fullName || emp.email || "Employee");
    setDocsEmployeeDocs(emp.employeeDocuments || {});
    setDocsEmployeeBank(emp.bank || {});
    setDocsEmployeePhoto(emp.photo || "");
    setShowDocsModal(true);
  };

  const openAgreementModal = (emp) => {
    setAgreementEmployee(emp);
    setShowAgreementModal(true);
  };

  const handleAssignVendors = async () => {
    try {
      // If special "None" is present, treat as [] (unassign all)
      const hasNone = selectedVendors.includes(V_NONE);
      const payload = {
        employeeId: selectedEmployeeId,
        vendorIds: hasNone ? [] : selectedVendors,
        supplierIds: selectedSuppliers.includes(V_NONE) ? [] : selectedSuppliers,
      };
      await API.put(`/admin/employees/${selectedEmployeeId}/assign-vendors`, payload);
      setShowModal(false);
      await fetchEmployees();
      setInfo(hasNone && selectedSuppliers.includes(V_NONE) ? "All vendors and suppliers unassigned" : "Assignments updated");
    } catch {
      setError("Failed to assign vendors");
    }
  };

  const filteredEmployees = useMemo(() => {
    const term = search.trim().toLowerCase();
    return employees.filter((emp) => {
      const matchesSearch =
        !term ||
        (emp.fullName || "").toLowerCase().includes(term) ||
        (emp.mobile || "").includes(term) ||
        (emp.email || "").toLowerCase().includes(term);
      const matchesStatus =
        statusFilter === "all"
          ? true
          : statusFilter === "approved"
          ? !!emp.approved
          : !emp.approved;
      return matchesSearch && matchesStatus;
    });
  }, [employees, search, statusFilter]);

  return (
    <div className="vm-page">
      <div className="vm-container">
        <div className="vm-topbar">
          <div className="vm-title-wrap">
            <div className="vm-logo">EM</div>
            <div>
              <div className="vm-title">Employee Management</div>
              <div className="vm-subtitle vm-subtitle-normal">Manage and monitor all employees</div>
            </div>
          </div>
          <div className="vm-search vm-search-top d-none d-md-block">
            <span className="material-symbols-outlined">search</span>
            <input
              type="text"
              placeholder="Search by name, mobile, or email"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="vm-actions">
            <button className="vm-toolbar-btn vm-toolbar-btn-primary" onClick={exportAgreementStatusCsv}>
              <span className="material-symbols-outlined">download</span>
              <span>Agreement CSV</span>
            </button>
            <button className="vm-toolbar-btn" onClick={fetchEmployees}>
              <span className="material-symbols-outlined">refresh</span>
              <span>Refresh</span>
            </button>
          </div>
        </div>

        <div className="vm-toolbar">
          <div>
            <div className="vm-tabs">
              <button className={`vm-tab ${statusFilter === "all" ? "active" : ""}`} onClick={() => setStatusFilter("all")}>
                <span>All Employees</span>
                <span className="vm-tab-badge blue">{stats.total}</span>
              </button>
              <button className={`vm-tab ${statusFilter === "approved" ? "active" : ""}`} onClick={() => setStatusFilter("approved")}>
                <span>Approved</span>
                <span className="vm-tab-badge green">{stats.approved}</span>
              </button>
              <button className={`vm-tab ${statusFilter === "pending" ? "active" : ""}`} onClick={() => setStatusFilter("pending")}>
                <span>Pending</span>
                <span className="vm-tab-badge amber">{stats.pending}</span>
              </button>
            </div>
          </div>
          <div className="vm-filter-row">
            <div className="vm-filter-stack">
              <div className="vm-search d-md-none">
                <span className="material-symbols-outlined">search</span>
                <input
                  type="text"
                  placeholder="Search employees..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Alerts */}
        {error && (
          <Alert variant="danger" onClose={() => setError("")} dismissible>
            {error}
          </Alert>
        )}
        {info && (
          <Alert variant="success" onClose={() => setInfo("")} dismissible>
            {info}
          </Alert>
        )}

        {/* Content */}
        {loading ? (
          <div className="text-center my-5">
            <Spinner animation="border" />
            <div className="mt-2 text-muted">Loading employees…</div>
          </div>
        ) : filteredEmployees.length === 0 ? (
          <Card className="p-4 text-center shadow-sm mt-3">
            <div style={{ fontSize: 32, lineHeight: 1 }}>🗂️</div>
            <h6 className="mt-2 mb-1">No employees found</h6>
            <p className="text-muted mb-0">Try adjusting filters or search.</p>
          </Card>
        ) : (
          <>
            <Row xs={1} md={2} xl={3} className="g-3 mt-1">
              {filteredEmployees.map((emp) => {
                const vendorsList =
                  emp.assignedVendors?.map((v) => v.fullName || v.email) || [];
                const suppliersList =
                  emp.assignedSuppliers?.map((s) => supplierLabel(s)) || [];
                const wp = emp.workProfile || {};
                const docs = emp.employeeDocuments || {};
                const bank = emp.bank || {};
                const uploadedDocs = ["aadhar", "pan", "addressProof", "resume"].filter(
                  (k) => Boolean(docs[k])
                );
                const agreement = emp.employeeAgreementSummary || emp.employeeAgreement || {};
                const agreementAccepted = !!agreement.accepted;

                return (
                  <Col key={emp._id}>
                    <div className="vm-entity-grid-card">
                      <div className="vm-entity-head">
                        <div className="vm-supplier-head">
                          <div className={`vm-avatar vm-avatar-${getAvatarTone(emp._id || emp.fullName)}`}>
                            {initials(emp.fullName || emp.email)}
                          </div>
                          <div className="vm-supplier-copy">
                            <div className="vm-person-name">{emp.fullName || "—"}</div>
                            <div className="vm-company-name">{emp.email || "—"}</div>
                            <div className="vm-code-chip">{emp.department?.name || "Employee"}</div>
                          </div>
                        </div>
                        <div className="vm-status-stack">
                          <span className={`vm-status-pill ${emp.approved ? "approved" : "pending"}`}>
                            {emp.approved ? "Approved" : "Pending"}
                          </span>
                          <span className={`vm-mini-pill ${agreementAccepted ? "verified" : "waiting"}`}>
                            Agreement: {agreementAccepted ? "signed" : "pending"}
                          </span>
                          <span className="vm-mini-pill neutral">
                            Docs: {uploadedDocs.length || 0}
                          </span>
                        </div>
                      </div>

                      <div className="vm-kv-grid">
                        <div className="vm-kv">
                          <span className="vm-k"><span className="material-symbols-outlined">smartphone</span>Mobile</span>
                          <span className="vm-v">{emp.mobile || "—"}</span>
                        </div>
                        <div className="vm-kv">
                          <span className="vm-k"><span className="material-symbols-outlined">badge</span>Department</span>
                          <span className="vm-v">{emp.department?.name || "—"}</span>
                        </div>
                        <div className="vm-kv">
                          <span className="vm-k"><span className="material-symbols-outlined">schedule</span>Shift</span>
                          <span className="vm-v">{wp.shiftStart && wp.shiftEnd ? `${wp.shiftStart} - ${wp.shiftEnd}` : "Not set"}</span>
                        </div>
                        <div className="vm-kv">
                          <span className="vm-k"><span className="material-symbols-outlined">payments</span>Salary</span>
                          <span className="vm-v">{wp.salaryMonthly ? `₹ ${wp.salaryMonthly}` : "Not set"}</span>
                        </div>
                        <div className="vm-kv">
                          <span className="vm-k"><span className="material-symbols-outlined">account_balance</span>Bank A/C</span>
                          <span className="vm-v vm-v-mono">{bank.accountNumber || "Not added"}</span>
                        </div>
                        <div className="vm-kv">
                          <span className="vm-k"><span className="material-symbols-outlined">tag</span>IFSC</span>
                          <span className="vm-v vm-v-mono">{bank.ifsc || "—"}</span>
                        </div>
                        <div className="vm-kv vm-kv-full">
                          <span className="vm-k"><span className="material-symbols-outlined">location_on</span>Office Location</span>
                          <span className="vm-v">{wp.officeAddress || (wp.officeLocation?.lat && wp.officeLocation?.lng ? "Pinned location set" : "Not set")}</span>
                        </div>
                      </div>

                      <div className="vm-meta-row">
                        <div className="vm-meta-chip">
                          <span className="material-symbols-outlined">calendar_today</span>
                          <span>Joined: {emp.createdAt ? new Date(emp.createdAt).toLocaleString("en-IN") : "—"}</span>
                        </div>
                        <div className="vm-meta-chip">
                          <span className="material-symbols-outlined">draw</span>
                          <span>Signed By: {agreement.signName || "—"}</span>
                        </div>
                      </div>

                      <div className="vm-po-grid">
                        <div className="vm-po-cell">
                          <span className="vm-k"><span className="material-symbols-outlined">store</span>Assigned Vendors</span>
                          {vendorsList.length ? (
                            <span className="vm-v">{vendorsList.slice(0, 2).join(", ")}{vendorsList.length > 2 ? ` +${vendorsList.length - 2}` : ""}</span>
                          ) : (
                            <span className="vm-v">No vendors assigned</span>
                          )}
                        </div>
                        <div className="vm-po-cell">
                          <span className="vm-k"><span className="material-symbols-outlined">description</span>Documents</span>
                          <span className="vm-v">{uploadedDocs.length ? `${uploadedDocs.length} uploaded` : "No docs"}</span>
                        </div>
                        <div className="vm-po-cell">
                          <span className="vm-k"><span className="material-symbols-outlined">local_shipping</span>Assigned Suppliers</span>
                          {suppliersList.length ? (
                            <span className="vm-v">{suppliersList.slice(0, 2).join(", ")}{suppliersList.length > 2 ? ` +${suppliersList.length - 2}` : ""}</span>
                          ) : (
                            <span className="vm-v">No suppliers assigned</span>
                          )}
                        </div>
                      </div>

                      <div className="vm-actions-wrap">
                        <Button
                          size="sm"
                          className="vm-action-btn vm-action-neutral"
                          onClick={() => navigate(`/admin/employees/${emp._id}/account`)}
                        >
                          <span className="material-symbols-outlined">badge</span>
                          <span>View Account</span>
                        </Button>
                        <Button
                          size="sm"
                          className="vm-action-btn vm-action-neutral"
                          onClick={() => navigate(`/admin/employees/${emp._id}/account?tab=history`)}
                        >
                          <span className="material-symbols-outlined">history</span>
                          <span>View History</span>
                        </Button>
                        <Button size="sm" className="vm-action-btn vm-action-blue" onClick={() => openAssignModal(emp)}>
                          <span className="material-symbols-outlined">group_add</span>
                          <span>Manage Vendors</span>
                        </Button>
                        <Button size="sm" className="vm-action-btn vm-action-cyan" onClick={() => openWorkModal(emp)}>
                          <span className="material-symbols-outlined">schedule</span>
                          <span>Job & Salary</span>
                        </Button>
                        <Button size="sm" className="vm-action-btn vm-action-neutral" onClick={() => openDocsModal(emp)}>
                          <span className="material-symbols-outlined">folder_shared</span>
                          <span>Bank & Docs</span>
                        </Button>
                        <Button size="sm" className="vm-action-btn vm-action-neutral" onClick={() => openAgreementModal(emp)}>
                          <span className="material-symbols-outlined">description</span>
                          <span>Agreement</span>
                        </Button>
                        <Button
                          size="sm"
                          className="vm-action-btn vm-action-neutral"
                          onClick={() => downloadEmployeeAgreementPdf({ employee: emp })}
                        >
                          <span className="material-symbols-outlined">download</span>
                          <span>Download PDF</span>
                        </Button>
                        <Button
                          size="sm"
                          className="vm-action-btn vm-action-green"
                          disabled={actionLoading === emp._id}
                          onClick={() => handleSendWelcome(emp._id)}
                        >
                          <span className="material-symbols-outlined">chat</span>
                          <span>{actionLoading === emp._id ? "Sending…" : "Send Welcome"}</span>
                        </Button>
                        {emp.approved ? (
                          <Button
                            size="sm"
                            className="vm-action-btn vm-action-amber"
                            disabled={actionLoading === emp._id}
                            onClick={() => handleTerminate(emp._id)}
                          >
                            <span className="material-symbols-outlined">block</span>
                            <span>{actionLoading === emp._id ? "Terminating…" : "Terminate"}</span>
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            className="vm-action-btn vm-action-success"
                            disabled={actionLoading === emp._id}
                            onClick={() => handleApprove(emp._id)}
                          >
                            <span className="material-symbols-outlined">check</span>
                            <span>{actionLoading === emp._id ? "Approving…" : "Approve"}</span>
                          </Button>
                        )}
                        <Button
                          size="sm"
                          className="vm-action-btn vm-action-danger-fill"
                          disabled={actionLoading === emp._id}
                          onClick={() => handleDelete(emp._id)}
                        >
                          <span className="material-symbols-outlined">delete</span>
                          <span>{actionLoading === emp._id ? "Deleting…" : "Delete"}</span>
                        </Button>
                      </div>
                    </div>
                  </Col>
                );
              })}
            </Row>
          </>
        )}

        {/* Assign Vendors Modal */}
        <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Assign Vendors</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <div className="text-muted small mb-1">Employee</div>
            <div className="fw-semibold">{selectedEmployeeName}</div>
          </div>

          <div className="d-flex justify-content-between align-items-center mb-2">
            <Form.Label className="m-0">Select Vendors</Form.Label>
            <div className="d-flex gap-2">
              <Button
                size="sm"
                variant="outline-secondary"
                onClick={() => setSelectedVendors([])}
              >
                Clear
              </Button>
              <Button
                size="sm"
                variant="outline-primary"
                onClick={() => setSelectedVendors(vendors.map((v) => v._id))}
              >
                Select All
              </Button>
            </div>
          </div>

          <Form.Select
            multiple
            value={selectedVendors}
            onChange={(e) => {
              const opts = Array.from(e.target.selectedOptions, (o) => o.value);
              if (opts.includes(V_NONE)) {
                // Selecting "None" → unassign all
                setSelectedVendors([V_NONE]); // keep only None visually
              } else {
                setSelectedVendors(opts.filter((x) => x !== V_NONE));
              }
            }}
            style={{ height: 220 }}
          >
            {/* Special option: None */}
            <option value={V_NONE}>None (Unassign all)</option>

            {/* Vendors */}
            {vendors.map((vendor) => (
              <option key={vendor._id} value={vendor._id}>
                {vendor.fullName || vendor.email}
              </option>
            ))}
          </Form.Select>

          <div className="d-flex justify-content-between align-items-center mt-4 mb-2">
            <Form.Label className="m-0">Select Suppliers</Form.Label>
            <div className="d-flex gap-2">
              <Button
                size="sm"
                variant="outline-secondary"
                onClick={() => setSelectedSuppliers([])}
              >
                Clear
              </Button>
              <Button
                size="sm"
                variant="outline-primary"
                onClick={() => setSelectedSuppliers(suppliers.map((s) => s._id))}
              >
                Select All
              </Button>
            </div>
          </div>

          <Form.Select
            multiple
            value={selectedSuppliers}
            onChange={(e) => {
              const opts = Array.from(e.target.selectedOptions, (o) => o.value);
              if (opts.includes(V_NONE)) {
                setSelectedSuppliers([V_NONE]);
              } else {
                setSelectedSuppliers(opts.filter((x) => x !== V_NONE));
              }
            }}
            style={{ height: 220 }}
          >
            <option value={V_NONE}>None (Unassign all)</option>
            {suppliers.map((supplier) => (
              <option key={supplier._id} value={supplier._id}>
                {supplierLabel(supplier)}
              </option>
            ))}
          </Form.Select>

          {/* Selected preview chips */}
          <div className="mt-3">
            <div className="text-muted small mb-1">Selected:</div>
            {selectedVendors.length === 0 ? (
              <span className="text-muted">—</span>
            ) : selectedVendors.includes(V_NONE) ? (
              <span className="badge bg-light text-dark border">None (Unassign all)</span>
            ) : (
              <div className="d-flex flex-wrap gap-2">
                {selectedVendors.map((id) => {
                  const v = vendors.find((x) => x._id === id);
                  return (
                    <span key={id} className="badge bg-light text-dark border">
                      {v?.fullName || v?.email || id}
                    </span>
                  );
                })}
              </div>
            )}
          </div>

          <div className="mt-3">
            <div className="text-muted small mb-1">Selected suppliers:</div>
            {selectedSuppliers.length === 0 ? (
              <span className="text-muted">—</span>
            ) : selectedSuppliers.includes(V_NONE) ? (
              <span className="badge bg-light text-dark border">None (Unassign all)</span>
            ) : (
              <div className="d-flex flex-wrap gap-2">
                {selectedSuppliers.map((id) => {
                  const s = suppliers.find((x) => x._id === id);
                  return (
                    <span key={id} className="badge bg-light text-dark border">
                      {supplierLabel(s) || id}
                    </span>
                  );
                })}
              </div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleAssignVendors}>
            Save
          </Button>
        </Modal.Footer>
        </Modal>

        {/* Documents Modal */}
        <Modal show={showDocsModal} onHide={() => setShowDocsModal(false)} centered dialogClassName="em-doc-modal-wrap">
          <Modal.Header closeButton>
            <Modal.Title>
              <span className="em-doc-title-icon material-symbols-outlined">folder_shared</span>
              Employee Documents
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="em-doc-modal-body">
            <div className="em-doc-emp-card">
              <div>
                <div className="em-doc-label">Employee</div>
                <div className="em-doc-emp-name">{docsEmployeeName}</div>
              </div>
              <div className="em-doc-avatar-wrap">
                {toFileURL(docsEmployeePhoto) ? (
                  <img src={toFileURL(docsEmployeePhoto)} alt={docsEmployeeName} className="em-doc-avatar" />
                ) : (
                  <span className="em-doc-avatar-fallback">{initials(docsEmployeeName)}</span>
                )}
              </div>
            </div>

            <div className="em-doc-bank-card">
              <div className="em-doc-section-title">Bank Details</div>
              <div className="em-doc-kv">
                <div>Account Name</div>
                <div>{docsEmployeeBank?.accountName || "Not added"}</div>
              </div>
              <div className="em-doc-kv">
                <div>Account Number</div>
                <div>{docsEmployeeBank?.accountNumber || "Not added"}</div>
              </div>
              <div className="em-doc-kv">
                <div>IFSC</div>
                <div>{docsEmployeeBank?.ifsc || "Not added"}</div>
              </div>
              <div className="em-doc-kv">
                <div>Bank Name</div>
                <div>{docsEmployeeBank?.bankName || "Not added"}</div>
              </div>
              <div className="em-doc-kv">
                <div>Branch</div>
                <div>{docsEmployeeBank?.branch || "Not added"}</div>
              </div>
            </div>

            <div className="em-doc-section-head">
              <span className="material-symbols-outlined">list_alt</span>
              <span>Document Inventory</span>
            </div>
            <div className="em-doc-inventory">
              {docInventory.map((doc) => {
              const url = toFileURL(docsEmployeeDocs?.[doc.key]);
              return (
                <div key={doc.key} className="em-doc-row">
                  <div className="em-doc-left">
                    <span className="material-symbols-outlined">{doc.icon}</span>
                    <span>{doc.label}</span>
                  </div>
                  {url ? (
                    <a className="em-doc-link" href={url} target="_blank" rel="noopener noreferrer">
                      View
                      <span className="material-symbols-outlined">open_in_new</span>
                    </a>
                  ) : (
                    <span className="em-doc-pill">Not uploaded</span>
                  )}
                </div>
              );
            })}
            </div>
          </Modal.Body>
          <Modal.Footer className="em-doc-footer">
            <Button variant="light" onClick={() => setShowDocsModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={() => setShowDocsModal(false)}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal show={showAgreementModal} onHide={() => setShowAgreementModal(false)} centered size="lg">
          <Modal.Header closeButton>
            <Modal.Title>Employee Agreement Status</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {agreementEmployee ? (
              <div className="em-agreement-modal">
                <div className="em-agreement-summary">
                  <div>
                    <div className="em-doc-label">Employee</div>
                    <div className="em-doc-emp-name">{agreementEmployee.fullName || agreementEmployee.email || "Employee"}</div>
                  </div>
                  <div className={`em-agreement-status-pill ${(agreementEmployee.employeeAgreementSummary?.accepted || agreementEmployee.employeeAgreement?.accepted) ? "ok" : "pending"}`}>
                    {(agreementEmployee.employeeAgreementSummary?.accepted || agreementEmployee.employeeAgreement?.accepted) ? "Signed" : "Pending"}
                  </div>
                </div>

                <div className="em-agreement-grid">
                  <div className="em-agreement-kv">
                    <span>Department</span>
                    <strong>{agreementEmployee.department?.name || "—"}</strong>
                  </div>
                  <div className="em-agreement-kv">
                    <span>Version</span>
                    <strong>{agreementEmployee.employeeAgreementSummary?.version || agreementEmployee.employeeAgreement?.version || 1}</strong>
                  </div>
                  <div className="em-agreement-kv">
                    <span>Signed By</span>
                    <strong>{agreementEmployee.employeeAgreementSummary?.signName || agreementEmployee.employeeAgreement?.signName || "—"}</strong>
                  </div>
                  <div className="em-agreement-kv">
                    <span>Accepted At</span>
                    <strong>
                      {(agreementEmployee.employeeAgreementSummary?.acceptedAt || agreementEmployee.employeeAgreement?.acceptedAt)
                        ? new Date(agreementEmployee.employeeAgreementSummary?.acceptedAt || agreementEmployee.employeeAgreement?.acceptedAt).toLocaleString("en-IN")
                        : "—"}
                    </strong>
                  </div>
                </div>

                {agreementEmployee.employeeAgreement?.signatureDataUrl ? (
                  <div className="em-agreement-signature-box">
                    <div className="em-doc-label">Digital Signature</div>
                    <img
                      src={agreementEmployee.employeeAgreement.signatureDataUrl}
                      alt="Employee signature"
                      className="em-agreement-signature-img"
                    />
                  </div>
                ) : (
                  <Alert variant="warning" className="mb-0">
                    Is employee ne abhi tak agreement sign nahi kiya hai.
                  </Alert>
                )}
              </div>
            ) : null}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setShowAgreementModal(false)}>
              Close
            </Button>
            {agreementEmployee ? (
              <Button variant="primary" onClick={() => downloadEmployeeAgreementPdf({ employee: agreementEmployee })}>
                Download PDF
              </Button>
            ) : null}
          </Modal.Footer>
        </Modal>

        {/* Work Profile Modal */}
        <Modal show={showWorkModal} onHide={() => setShowWorkModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Set Job Time & Salary</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="mb-3">
              <div className="text-muted small mb-1">Employee</div>
              <div className="fw-semibold">{workEmployeeName}</div>
            </div>

            <Row className="g-3">
              <Col md={6}>
                <Form.Label>Shift Start (HH:mm)</Form.Label>
                <Form.Control
                  type="time"
                  value={workForm.shiftStart}
                  onChange={(e) => setWorkForm({ ...workForm, shiftStart: e.target.value })}
                />
              </Col>
              <Col md={6}>
                <Form.Label>Shift End (HH:mm)</Form.Label>
                <Form.Control
                  type="time"
                  value={workForm.shiftEnd}
                  onChange={(e) => setWorkForm({ ...workForm, shiftEnd: e.target.value })}
                />
              </Col>
              <Col md={6}>
                <Form.Label>Monthly Salary</Form.Label>
                <Form.Control
                  type="number"
                  value={workForm.salaryMonthly}
                  onChange={(e) => setWorkForm({ ...workForm, salaryMonthly: e.target.value })}
                />
              </Col>
              <Col md={6}>
                <Form.Label>Hourly Rate</Form.Label>
                <Form.Control
                  type="number"
                  value={workForm.hourlyRate}
                  onChange={(e) => setWorkForm({ ...workForm, hourlyRate: e.target.value })}
                />
              </Col>
              <Col md={6}>
                <Form.Label>Overtime Rate</Form.Label>
                <Form.Control
                  type="number"
                  value={workForm.overtimeRate}
                  onChange={(e) => setWorkForm({ ...workForm, overtimeRate: e.target.value })}
                />
              </Col>
              <Col md={6}>
                <Form.Label>Late Grace (minutes)</Form.Label>
                <Form.Control
                  type="number"
                  value={workForm.lateGraceMinutes}
                  onChange={(e) => setWorkForm({ ...workForm, lateGraceMinutes: e.target.value })}
                />
              </Col>
              <Col md={6}>
                <Form.Label>Attendance Mode</Form.Label>
                <Form.Select
                  value={workForm.attendanceMode}
                  onChange={(e) => setWorkForm({ ...workForm, attendanceMode: e.target.value })}
                >
                  <option value="office">Office (strict geofence)</option>
                  <option value="field">Field (tracking + meeting mode)</option>
                </Form.Select>
              </Col>
              <Col md={6}>
                <Form.Label>Office Radius (meters)</Form.Label>
                <Form.Control
                  type="number"
                  min={10}
                  value={workForm.officeRadiusMeters}
                  onChange={(e) => setWorkForm({ ...workForm, officeRadiusMeters: e.target.value })}
                />
              </Col>
              <Col md={12}>
                <Form.Label>Office Address</Form.Label>
                <Form.Control
                  value={workForm.officeAddress}
                  onChange={(e) => setWorkForm({ ...workForm, officeAddress: e.target.value })}
                />
              </Col>
              <Col md={12}>
                <Button
                  variant="outline-primary"
                  type="button"
                  onClick={fillOfficeFromCurrentLocation}
                  disabled={locLoading}
                >
                  {locLoading ? "Fetching..." : "Use Current Location"}
                </Button>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowWorkModal(false)}>
              Cancel
            </Button>
            <Button variant="success" onClick={handleSaveWorkProfile}>
              Save
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </div>
  );
};

export default AdminEmployeeList;
